import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { vendasAPI, empresasAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ShoppingCart, CheckCircle, Clock, XCircle, TrendingUp, Calendar, ChevronLeft, ChevronRight, MapPin, Building2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { format, startOfMonth, endOfMonth, isWithinInterval, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    total: 0,
    instaladas: 0,
    pendentes: 0,
    canceladas: 0,
    enviadasBko: 0,
    devolvidas: 0
  });
  const [cidadeStats, setCidadeStats] = useState([]);
  const [cidadePorEmpresa, setCidadePorEmpresa] = useState({});
  const [empresas, setEmpresas] = useState({});
  const [loading, setLoading] = useState(true);
  const [mesAtual, setMesAtual] = useState(new Date());
  const [modoCustom, setModoCustom] = useState(false);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  useEffect(() => {
    loadStats();
  }, [mesAtual, modoCustom, dataInicio, dataFim]);

  const loadStats = async () => {
    try {
      // Filtra no servidor (com 1 dia de folga) em vez de baixar TODAS as vendas; o filtro exato continua abaixo
      let ini, fim;
      if (modoCustom && dataInicio && dataFim) {
        ini = new Date(dataInicio + 'T00:00:00');
        fim = new Date(dataFim + 'T23:59:59');
      } else {
        ini = startOfMonth(mesAtual);
        fim = endOfMonth(mesAtual);
      }
      const params = {
        data_inicio: new Date(ini.getTime() - 86400000).toISOString(),
        data_fim: new Date(fim.getTime() + 86400000).toISOString(),
      };
      const [response, empRes] = await Promise.all([vendasAPI.list(params), empresasAPI.list()]);
      const todasVendas = response.data;
      const empDict = {};
      (empRes.data || []).forEach(e => { empDict[e.id] = e.nome; });
      setEmpresas(empDict);
      
      let vendasFiltradas;
      if (modoCustom && dataInicio && dataFim) {
        const ini = new Date(dataInicio + 'T00:00:00');
        const fim = new Date(dataFim + 'T23:59:59');
        vendasFiltradas = todasVendas.filter(v => {
          const d = new Date(v.criado_em);
          return d >= ini && d <= fim;
        });
      } else {
        const inicioMes = startOfMonth(mesAtual);
        const fimMes = endOfMonth(mesAtual);
        vendasFiltradas = todasVendas.filter(v => {
          const dataVenda = new Date(v.criado_em);
          return isWithinInterval(dataVenda, { start: inicioMes, end: fimMes });
        });
      }

      setStats({
        total: vendasFiltradas.length,
        instaladas: vendasFiltradas.filter(v => v.status === 'Instalada').length,
        pendentes: vendasFiltradas.filter(v => v.status === 'Pendência de Instalação').length,
        canceladas: vendasFiltradas.filter(v => v.status === 'Cancelamento').length,
        enviadasBko: vendasFiltradas.filter(v => v.status === 'Enviado ao BKO').length,
        devolvidas: vendasFiltradas.filter(v => v.status === 'Devolvida').length,
      });

      // Calcular vendas por cidade
      const cidadeMap = {};
      const cidadeDisplay = {};
      const normalizar = (t) => t?.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().trim() || '';
      vendasFiltradas.forEach(v => {
        const cidade = (v.endereco?.cidade || '').toUpperCase().trim();
        const uf = (v.endereco?.estado || '').toUpperCase().trim();
        if (cidade) {
          const cidadeNorm = normalizar(cidade);
          const key = uf ? `${cidadeNorm}/${uf}` : cidadeNorm;
          const display = uf ? `${cidade}/${uf}` : cidade;
          cidadeMap[key] = (cidadeMap[key] || 0) + 1;
          if (!cidadeDisplay[key] || display.length > cidadeDisplay[key].length) {
            cidadeDisplay[key] = display;
          }
        }
      });
      const totalVendas = vendasFiltradas.length || 1;
      const cidadesOrdenadas = Object.entries(cidadeMap)
        .map(([key, qtd]) => ({ nome: cidadeDisplay[key], qtd, pct: ((qtd / totalVendas) * 100).toFixed(1) }))
        .sort((a, b) => b.qtd - a.qtd);
      setCidadeStats(cidadesOrdenadas);

      // Calcular vendas por cidade POR EMPRESA (apenas INSTALADAS)
      const porEmpresa = {};
      const vendasInstaladas = vendasFiltradas.filter(v => v.status === 'Instalada');
      vendasInstaladas.forEach(v => {
        const empId = v.empresa_id || 'sem_empresa';
        const empNome = empDict[empId] || 'Sem Empresa';
        const cidade = (v.endereco?.cidade || '').toUpperCase().trim();
        const uf = (v.endereco?.estado || '').toUpperCase().trim();
        if (cidade) {
          if (!porEmpresa[empNome]) porEmpresa[empNome] = { map: {}, display: {}, total: 0 };
          const cidadeNorm = normalizar(cidade);
          const key = uf ? `${cidadeNorm}/${uf}` : cidadeNorm;
          const display = uf ? `${cidade}/${uf}` : cidade;
          porEmpresa[empNome].map[key] = (porEmpresa[empNome].map[key] || 0) + 1;
          porEmpresa[empNome].total += 1;
          if (!porEmpresa[empNome].display[key] || display.length > porEmpresa[empNome].display[key].length) {
            porEmpresa[empNome].display[key] = display;
          }
        }
      });
      // Converter para arrays ordenadas
      const resultado = {};
      Object.entries(porEmpresa).forEach(([empNome, data]) => {
        resultado[empNome] = Object.entries(data.map)
          .map(([key, qtd]) => ({ nome: data.display[key], qtd, pct: ((qtd / data.total) * 100).toFixed(1) }))
          .sort((a, b) => b.qtd - a.qtd);
      });
      setCidadePorEmpresa(resultado);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const mesAnterior = () => { setModoCustom(false); setMesAtual(prev => subMonths(prev, 1)); };
  const mesProximo = () => { setModoCustom(false); setMesAtual(prev => addMonths(prev, 1)); };
  const mesHoje = () => { setModoCustom(false); setMesAtual(new Date()); };

  const statCards = [
    { 
      title: 'Total de Vendas', 
      value: stats.total, 
      icon: ShoppingCart, 
      color: 'from-red-500 to-purple-600',
      textColor: 'text-red-500'
    },
    { 
      title: 'Enviadas ao BKO', 
      value: stats.enviadasBko, 
      icon: Clock, 
      color: 'from-purple-500 to-purple-600',
      textColor: 'text-purple-500'
    },
    { 
      title: 'Instaladas', 
      value: stats.instaladas, 
      icon: CheckCircle, 
      color: 'from-green-500 to-green-600',
      textColor: 'text-green-600'
    },
    { 
      title: 'Pendentes', 
      value: stats.pendentes, 
      icon: Clock, 
      color: 'from-amber-500 to-orange-600',
      textColor: 'text-amber-600'
    },
    { 
      title: 'Devolvidas', 
      value: stats.devolvidas, 
      icon: XCircle, 
      color: 'from-purple-500 to-purple-600',
      textColor: 'text-purple-500'
    },
    { 
      title: 'Canceladas', 
      value: stats.canceladas, 
      icon: XCircle, 
      color: 'from-red-600 to-red-800',
      textColor: 'text-red-600'
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-red-500 via-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
            <TrendingUp className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-500">
              Bem-vindo, <span className="font-semibold">{user?.nome}</span>!
            </p>
          </div>
        </div>
        
        {/* Seletor de Período */}
        <div className="flex flex-col items-end gap-2">
          <div className="bg-gradient-to-r from-red-500 via-purple-500 to-purple-600 text-white px-4 py-2 rounded-xl shadow-lg flex items-center gap-2">
            <Button size="sm" variant="ghost" className="text-white hover:bg-red-400/30 p-1 h-8 w-8" onClick={mesAnterior}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2 cursor-pointer" onClick={mesHoje}>
              <Calendar className="w-5 h-5" />
              <p className="text-sm font-bold capitalize">
                {modoCustom ? `${dataInicio?.split('-').reverse().join('/')} - ${dataFim?.split('-').reverse().join('/')}` : format(mesAtual, "MMMM 'de' yyyy", { locale: ptBR })}
              </p>
            </div>
            <Button size="sm" variant="ghost" className="text-white hover:bg-red-400/30 p-1 h-8 w-8" onClick={mesProximo}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Input type="date" value={dataInicio} onChange={e => { setDataInicio(e.target.value); setModoCustom(true); }} className="h-8 text-xs w-36" data-testid="dash-data-inicio" />
            <Input type="date" value={dataFim} onChange={e => { setDataFim(e.target.value); setModoCustom(true); }} className="h-8 text-xs w-36" data-testid="dash-data-fim" />
          </div>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{stat.title}</p>
                    <p className={`text-4xl font-bold mt-2 ${stat.textColor}`}>
                      {loading ? '...' : stat.value}
                    </p>
                  </div>
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Vendas por Cidade - Por Empresa */}
      {Object.keys(cidadePorEmpresa).length > 0 && (
        <div className="space-y-4" data-testid="vendas-por-cidade">
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-bold text-gray-900">Instaladas por Cidade</h2>
            <span className="text-sm font-semibold text-purple-400 bg-purple-500/10 px-3 py-1 rounded-full capitalize">
              {modoCustom ? `${dataInicio?.split('-').reverse().join('/')} - ${dataFim?.split('-').reverse().join('/')}` : format(mesAtual, "MMMM yyyy", { locale: ptBR })}
            </span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {Object.entries(cidadePorEmpresa)
              .sort((a, b) => b[1].reduce((s, c) => s + c.qtd, 0) - a[1].reduce((s, c) => s + c.qtd, 0))
              .map(([empNome, cidades]) => {
                const totalEmp = cidades.reduce((s, c) => s + c.qtd, 0);
                const COLORS = ['#ef4444', '#a855f7', '#f97316', '#3b82f6', '#10b981', '#ec4899', '#6366f1', '#14b8a6'];
                return (
                  <Card key={empNome}>
                    <CardHeader className="pb-2 pt-4 px-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-purple-500" />
                          <CardTitle className="text-base">{empNome}</CardTitle>
                        </div>
                        <span className="text-xs font-bold text-red-500 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded-full">
                          {totalEmp} {totalEmp === 1 ? 'venda' : 'vendas'} · {cidades.length} {cidades.length === 1 ? 'cidade' : 'cidades'}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="px-5 pb-4 pt-0">
                      <div className="w-full" style={{ height: Math.max(120, cidades.length * 44) }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={cidades} layout="vertical" margin={{ top: 4, right: 70, left: 0, bottom: 4 }}>
                            <XAxis type="number" hide />
                            <YAxis
                              type="category" dataKey="nome" width={160}
                              tick={({ x, y, payload, index }) => (
                                <g transform={`translate(${x},${y})`}>
                                  <text x={-6} y={0} dy={4} textAnchor="end" fill="#9ca3af" fontSize={11} fontWeight={700}>{index + 1}.</text>
                                  <text x={0} y={0} dy={4} textAnchor="start" fill={COLORS[index % COLORS.length]} fontSize={12} fontWeight={600}>{payload.value}</text>
                                </g>
                              )}
                              axisLine={false} tickLine={false}
                            />
                            <Tooltip
                              content={({ active, payload: pl }) => {
                                if (active && pl && pl.length) {
                                  const d = pl[0].payload;
                                  return (
                                    <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-xl text-sm border border-gray-700">
                                      <p className="font-bold">{d.nome}</p>
                                      <p><span className="text-red-400 font-bold">{d.qtd}</span> {d.qtd === 1 ? 'venda' : 'vendas'} ({d.pct}%)</p>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Bar dataKey="qtd" radius={[0, 8, 8, 0]} barSize={26}>
                              {cidades.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                              <LabelList dataKey="qtd" position="right"
                                content={({ x, y, width, value, index }) => (
                                  <text x={x + width + 6} y={y + 14} fill="#e5e7eb" fontSize={12} fontWeight={700}>
                                    {value} <tspan fill="#9ca3af" fontSize={10}>({cidades[index]?.pct}%)</tspan>
                                  </text>
                                )}
                              />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        </div>
      )}

      {/* Informações do Perfil */}
      <Card>
        <CardHeader>
          <CardTitle>Informações do Usuário</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Nome</p>
              <p className="font-semibold">{user?.nome}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">E-mail</p>
              <p className="font-semibold">{user?.email}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Perfil</p>
              <p className="font-semibold">{user?.perfil}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
